/**
 * sqlite3mt lifecycle integration factory
 *
 * §96 §2.2–§2.5, §4, §5, §7.0, §11.1.
 *
 * This module is the integration seam where all protocol modules connect.
 * It provides factory functions for:
 *   1. Allocating the SAB set (delegates to sab-layout.ts)
 *   2. Creating initial Control Worker state (§5)
 *   3. Creating Client Worker per-connection state (§11.1)
 *   4. Wiring the ControlWorkerRuntimeHooks to protocol handlers
 *   5. Initializing all SAB slots to a clean pre-bootstrap state
 *
 * This module does NOT own the worker thread lifecycle (that belongs to
 * the platform-specific worker bridge). It owns the *state* and *wiring*
 * that makes a multi-worker SQLite runtime coherent.
 *
 * Invariants verified by tests:
 *   - SAB set is allocated with correct sizes
 *   - Control Worker state is initialized to spec-consistent defaults
 *   - Client Worker state is initialized per §11.1
 *   - SAB4 bootstrap reset zeroes all per-connection arrays (R38/R71/R76)
 *   - SAB3 lock reset zeroes the lock region (R72)
 *   - CONTROL_READY starts at 0 (R62)
 *   - runtimeEpoch advances strictly monotonically (R74/R75)
 *   - Fresh generation reinitializes all volatile state (R76)
 */

import {
    allocateSabSet,
    computeSabSizes,
    sab1CtrlView,
    sab1SlotMetaView,
    sab1StoreSlotGen,
    sab1SlotDataView,
    sab4ResetConnSlots,
    sab3ResetLocks,
    sab4RuntimeEpochView,
    sab4ScalarView,
    sab2SizeView,
    sab2MaxWalByteLength,
    sab2DataView,
    sab3ShmView,
    sab3LockView,
    NUM_SLOTS,
    MAX_CONN,
    DEFAULT_PAGE_SIZE,
    normalizeWalShadowSizeConfig,
    SAB1_DB_PAGE_COUNT_I32,
    Sab4Scalar,
    SlotMetaI32,
    type SabConfig,
    type SabSizes,
} from './sab-layout'
import type { SdkObservers } from '../observers'
import {
    chooseFreshRuntimeEpoch,
    runBootstrap,
    type BootstrapOrchestratorHooks,
    type Deferred,
    type DurableReconcileIntent,
    type PendingBaseAdvance,
} from './bootstrap'
import { onWalCommitSync } from './commit-registration'
import {
    endCheckpointQuiescentWindow,
    beginCheckpointQuiescentWindow,
} from './checkpoint'
import {
    abortIfFatal,
    enterFatalRuntimeState,
    type FatalWakeState,
} from './control'
import {
    createControlWorkerRuntime,
    type ControlWorkerRuntime,
    type ControlWorkerRuntimeHooks,
} from './control-runtime'
import {
    acceptWalCycleFromSnapshot,
    applyBackfilledPending,
} from './dbview'
import { sameWalSalt } from './types'
import type {
    DurableLocalSnapshot,
    DurableLocalStore,
} from './durable'
import {
    getDbPageHash,
    type FullManifestOperations,
    type SecureBlobRpcBridge,
} from './manifest-ops'
import { applyRemoteHeadIfNewer } from './remote-head'
import {
    captureDurableSnapshot,
    persistSnapshotSerial,
    requestPersistLocalSnapshot,
    scheduleSyncRetry,
    type TimeoutScheduler,
} from './snapshot-persistence'
import { publishFaultState } from './slot'
import {
    launchCheckpointIfNeeded,
    handleCheckpointDone,
    handleFaultRequests,
    createFaultReadAheadState,
    maxCheckpointMode,
    type RunCheckpointMessage,
    type FaultReadAheadState,
} from './worker-bridge'
import type { HashPageFn } from './wal-scan'
import { publishFrontierStateToSabIfChanged, readerNeedsFrontierWait } from './frontier'
import type { SyncEngineKickReason } from './sync-engine'

import type {
    Root,
    RootId,
    PublicHead,
    Pgno,
    PageHash,
    FrameMax,
    Delta,
    WalSnapshot,
    ClientWorkerConnState,
    ControlWorkerState,
    CheckpointMode,
} from './types'
import { SlotState } from './types'

const WRITE_GATE_REASON_LIFECYCLE_FATAL = 2
const SNAPSHOT_PERSIST_FATAL_REASON = 'SNAPSHOT_PERSIST_FAILED'
const CHECKPOINT_LAUNCH_FATAL_REASON = 'CHECKPOINT_LAUNCH_FAILED'
const SHUTDOWN_FATAL_REASON = 'LIFECYCLE_SHUTDOWN'
const WAL_TRUNCATE_CHECKPOINT_THRESHOLD_RATIO = 0.5

// ── §5 Full Control Worker volatile state ────────────────────────

/**
 * Complete mutable Control Worker state — §5.
 *
 * This extends ControlWorkerState (DB-view + workspace-view + WAL integration)
 * with all volatile runtime flags, busy states, and tracking counters.
 */
export interface ControlWorkerRuntimeState extends ControlWorkerState {
    // Remote
    remoteHead: PublicHead

    // Durable tracking
    snapshotPersistTail: Promise<void>
    snapshotPersistBusy: boolean
    snapshotPersistWanted: boolean

    // Base/head-advance barrier (R32/R33)
    pendingBaseAdvance: PendingBaseAdvance | null
    inflightCommitRegistrations: number
    headAdvancePreparing: boolean
    headAdvancePrepareWaiter: Deferred<void> | null
    commitRegistrationDrainWaiter: Deferred<void> | null

    // Checkpoint quiescence (R36)
    checkpointQuiescentBusy: boolean

    // Staging / cache
    stagedCas: Map<PageHash, Uint8Array>
    stagedManifestNodes: Map<string, Uint8Array>
    faultReadAhead?: FaultReadAheadState

    // Busy flags
    pushBusy: boolean
    mergeBusy: boolean
    checkpointBusy: boolean
    compactBusy: boolean
    compactWanted: boolean

    // In-flight snapshots
    pushSnap: null | {
        expectedRoot: Root
        expectedVersion: bigint
        expectedPageCount: number
        overlaySnapshot: Map<Pgno, PageHash>
    }
    reconcileIntent: DurableReconcileIntent | null

    // Retry / checkpoint
    syncRetryScheduled: boolean
    syncRetryDelayMs: number
    checkpointWanted: boolean
    checkpointWantedMode: CheckpointMode | null
    checkpointInflightSeq: number
    checkpointNoProgressCount: number
    externalClientRequestCount: number

    // Remote refresh ordering (§13)
    remoteRefreshReqSeq: bigint
    remoteRefreshAppliedReqSeq: bigint

    // Errors
    syncFatalError: unknown | null
    lastSyncError: unknown | null
    recoveryChangedState: boolean

    // Runtime generation
    runtimeEpoch: bigint
    sdkObservers?: SdkObservers
}

// ── SAB set + typed views ────────────────────────────────────────

/**
 * The complete SAB set with pre-created typed views.
 *
 * All protocol modules receive this reference rather than
 * individual SharedArrayBuffers.
 */
export interface SabSet {
    readonly sab1: SharedArrayBuffer
    readonly sab2: SharedArrayBuffer
    readonly sab3: SharedArrayBuffer
    readonly sab4: SharedArrayBuffer
    readonly pageSize: number
    readonly initialWalSize: number
    readonly maxWalSize: number
}

// ── §7.0 Fresh runtime initialization ────────────────────────────

/**
 * Create a fresh, empty Control Worker runtime state.
 *
 * §5 / §7.0 / R76: All volatile state starts at known defaults.
 * DB-view and workspace-view are zeroed — bootstrap fills them in.
 */
export function createInitialControlWorkerRuntimeState(
    runtimeEpoch: bigint
): ControlWorkerRuntimeState {
    // Sentinel empty root — bootstrap will replace with real data
    const emptyRoot: Root = {
        rootId: '' as RootId,
        meta: {
            manifestRootId: '',
            pageCount: 0,
            parentPublicRootId: null,
        },
    }

    const emptyHead: PublicHead = {
        root: emptyRoot,
        version: 0n,
    }

    return {
        // DB-view
        dbBaseRoot: emptyRoot,
        dbOverlay: new Map(),
        dbPageCount: 0,

        // Workspace-view
        workspaceBaseHead: emptyHead,
        workspaceOverlay: new Map(),
        workspacePageCount: 0,

        // WAL integration
        pendingDbDeltas: new Map(),
        currentWalSalt0: null,
        currentWalSalt1: null,
        integratedFrameMax: 0,
        lastWalSnapshot: null,
        localCommitSeq: 0n,

        // Remote
        remoteHead: emptyHead,

        // Durable tracking
        snapshotPersistTail: Promise.resolve(),
        snapshotPersistBusy: false,
        snapshotPersistWanted: false,

        // Base/head-advance barrier
        pendingBaseAdvance: null,
        inflightCommitRegistrations: 0,
        headAdvancePreparing: false,
        headAdvancePrepareWaiter: null,
        commitRegistrationDrainWaiter: null,

        // Checkpoint quiescence
        checkpointQuiescentBusy: false,

        // Staging
        stagedCas: new Map(),
        stagedManifestNodes: new Map(),

        // Busy flags
        pushBusy: false,
        mergeBusy: false,
        checkpointBusy: false,
        compactBusy: false,
        compactWanted: false,

        // In-flight snapshots
        pushSnap: null,
        reconcileIntent: null,

        // Retry / checkpoint
        syncRetryScheduled: false,
        syncRetryDelayMs: 1000,
        checkpointWanted: false,
        checkpointWantedMode: null,
        checkpointInflightSeq: 0,
        checkpointNoProgressCount: 0,
        externalClientRequestCount: 0,

        // Remote refresh
        remoteRefreshReqSeq: 0n,
        remoteRefreshAppliedReqSeq: 0n,

        // Errors
        syncFatalError: null,
        lastSyncError: null,
        recoveryChangedState: false,

        // Runtime generation
        runtimeEpoch,
    }
}

// ── §11.1 Client Worker connection state factory ─────────────────

/**
 * Create a fresh Client Worker per-connection state.
 *
 * §11.1: Each connection gets a unique connId (index into SAB4 arrays),
 * attaches to the current runtimeEpoch, and starts with no locks held.
 *
 * R61: connId must be unique within the same runtime generation.
 * R40: nextCommitReqId starts at 0 and is 64-bit monotonic.
 */
export function createClientWorkerConnState(
    connId: number,
    runtimeEpoch: bigint
): ClientWorkerConnState {
    if (connId < 0 || connId >= MAX_CONN) {
        throw new RangeError(
            `connId ${connId} out of range [0, ${MAX_CONN - 1}]`
        )
    }

    return {
        attachedRuntimeEpoch: runtimeEpoch,
        connId,
        nextCommitReqId: 0n,
        admittedWriteGateSeq: null,
        admittedCheckpointGateSeq: null,
        pendingCommitFrame: 0,
        txStartFrame: 0,
        holdsWalWrite: false,
        fatalIoMode: false,
        readerSalt0: 0,
        readerSalt1: 0,
        readerFrameMax: 0,
        myLocks: new Int8Array(8).fill(-1),
    }
}

// ── §7.0 SAB pre-bootstrap initialization ────────────────────────

/**
 * Initialize all SAB regions to a clean pre-bootstrap state.
 *
 * R62: CONTROL_READY = 0 (bootstrap in progress).
 * R38/R71: Zero all per-connection arrays in SAB4.
 * R72: Zero SAB3 lock region.
 * §4.1: All slots start EMPTY with gen=0.
 *
 * This must be called BEFORE bootstrap starts, and BEFORE any
 * workers are given access to the SABs.
 */
export function initializeSabSetForBootstrap(sabs: SabSet): void {
    const { sab1, sab2, sab3, sab4, pageSize } = sabs

    // SAB1: all slots to EMPTY, gen=0, pgno=0
    const ctrl = sab1CtrlView(sab1)
    Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, 0)

    for (let i = 0; i < NUM_SLOTS; i++) {
        const meta = sab1SlotMetaView(sab1, i, pageSize)
        Atomics.store(meta, SlotMetaI32.LOCK, 0)
        Atomics.store(meta, SlotMetaI32.STATE, SlotState.EMPTY)
        Atomics.store(meta, SlotMetaI32.PGNO, 0)
        Atomics.store(meta, SlotMetaI32.WAITSEQ, 0)
        sab1StoreSlotGen(sab1, i, 0n, pageSize)
        // Clear page data
        const data = sab1SlotDataView(sab1, i, pageSize)
        data.fill(0)
    }

    // SAB2: zero WAL shadow size and data
    sab2SizeView(sab2)[0] = 0
    sab2DataView(sab2).fill(0)

    // SAB3: zero SHM data + locks
    sab3ShmView(sab3).fill(0)
    sab3ResetLocks(sab3)

    // SAB4: zero per-connection arrays + set scalars to pre-bootstrap
    sab4ResetConnSlots(sab4)

    const epoch = sab4RuntimeEpochView(sab4)
    Atomics.store(epoch, 0, 0n)

    const scalars = sab4ScalarView(sab4)
    // R62: CONTROL_READY = 0 until bootstrap completes
    Atomics.store(scalars, Sab4Scalar.CONTROL_READY, 0)
    Atomics.store(scalars, Sab4Scalar.CONTROL_READY_WAITSEQ, 0)
    // Frontier: invalid until bootstrap publishes
    Atomics.store(scalars, Sab4Scalar.FRONTIER_VALID, 0)
    Atomics.store(scalars, Sab4Scalar.FRONTIER_SEQ, 0)
    Atomics.store(scalars, Sab4Scalar.FRONTIER_SALT0, 0)
    Atomics.store(scalars, Sab4Scalar.FRONTIER_SALT1, 0)
    Atomics.store(scalars, Sab4Scalar.FRONTIER_INTEGRATED_MAX, 0)
    Atomics.store(scalars, Sab4Scalar.FRONTIER_WAITSEQ, 0)
    // Fatal flag clear
    Atomics.store(scalars, Sab4Scalar.FATAL_FLAG, 0)
    // Gates: write gate closed during bootstrap (no commits yet)
    Atomics.store(scalars, Sab4Scalar.WRITE_GATE_CLOSED, 1)
    Atomics.store(scalars, Sab4Scalar.WRITE_GATE_REASON, 0)
    Atomics.store(scalars, Sab4Scalar.WRITE_GATE_WAITSEQ, 0)
    // Checkpoint gate closed during bootstrap
    Atomics.store(scalars, Sab4Scalar.CHECKPOINT_GATE_CLOSED, 1)
    Atomics.store(scalars, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ, 0)
    // WAL_WRITE_HOLDERS = 0 (already done by sab4ResetConnSlots)
    Atomics.store(scalars, Sab4Scalar.WAL_WRITE_HOLDERS, 0)
    Atomics.store(scalars, Sab4Scalar.MAIN_DB_READER_COUNT, 0)
    Atomics.store(scalars, Sab4Scalar.MAIN_DB_WRITER_CONN, 0)
}

// ── §7.0 Epoch management ────────────────────────────────────────

let nextEpochCounter = 1n

/**
 * Generate a fresh runtime epoch.
 *
 * R74/R75: Each bootstrap/recovery must run under a unique generation.
 * The epoch counter is monotonic within the JS process.
 */
export function generateRuntimeEpoch(): bigint {
    return nextEpochCounter++
}

/**
 * Reset the epoch counter.
 * Only for testing — production code never resets.
 */
export function resetEpochCounterForTesting(): void {
    nextEpochCounter = 1n
}

// ── §4 SabSet allocation ─────────────────────────────────────────

/**
 * Allocate a new SAB set and initialize it for bootstrap.
 *
 * This is the main entry point for creating the shared memory
 * regions that all workers will use.
 */
export function createAndInitializeSabSet(
    config: SabConfig = {}
): SabSet {
    const pageSize = config.pageSize ?? DEFAULT_PAGE_SIZE
    const { initialWalSize, maxWalSize } = normalizeWalShadowSizeConfig(config)

    const { sab1, sab2, sab3, sab4 } = allocateSabSet({ pageSize, initialWalSize, maxWalSize })

    const sabs: SabSet = {
        sab1, sab2, sab3, sab4,
        pageSize,
        initialWalSize,
        maxWalSize,
    }

    initializeSabSetForBootstrap(sabs)
    return sabs
}

export interface ControlWorkerLifecycleOptions {
    durable: DurableLocalStore
    manifest: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'>
    rpc: Pick<SecureBlobRpcBridge, 'rpcGetBlob' | 'rpcGetHeadVersioned'>
        & Partial<Pick<SecureBlobRpcBridge, 'rpcGetBlobs'>>
    hashPage: HashPageFn
    loadWalBytesForBootstrap(): Uint8Array | Promise<Uint8Array>
    primeLiveWalViewForBootstrap(): Promise<void>
    tryReadWalSnapshotStableOrNull(): WalSnapshot | null
    postRunCheckpoint(msg: RunCheckpointMessage): void
    kickSyncEngine(reason: SyncEngineKickReason): void
    yieldOneTaskTurnForPendingCommitMessages(): Promise<void>
    onShutdown?(): void | Promise<void>
    onSnapshotPersistError?(error: unknown): void
    refreshRemoteHeadAfterBootstrap?(): void
    maybeCompactDbBaseRoot?(): void
    readLiveWalSnapshot?(): WalSnapshot | null
    maxPassiveCheckpointNoProgress?: number
    faultReadAheadPages?: number
    nowMs?(): number
    onBootstrapProgress?(message: string, details?: Record<string, unknown>): void
    sabConfig?: SabConfig
    sabs?: SabSet
    state?: ControlWorkerRuntimeState
    setTimeoutFn?: TimeoutScheduler
}

export interface ControlWorkerLifecycle {
    readonly sabs: SabSet
    readonly state: ControlWorkerRuntimeState
    readonly runtime: ControlWorkerRuntime
    readonly runtimeHooks: ControlWorkerRuntimeHooks
    advanceDbHead(): WalSnapshot
    enterFatalRuntime(error: unknown, reason: string): boolean
    maybeRequestCheckpoint(reason: string): Promise<boolean>
    requestPersistLocalSnapshot(): Promise<void> | undefined
}

export function createControlWorkerLifecycle(
    options: ControlWorkerLifecycleOptions
): ControlWorkerLifecycle {
    const sabs = options.sabs ?? createAndInitializeSabSet(options.sabConfig)
    const state = options.state ?? createInitialControlWorkerRuntimeState(0n)
    const maxPassiveCheckpointNoProgress = options.maxPassiveCheckpointNoProgress ?? 3
    state.faultReadAhead ??= createFaultReadAheadState(options.faultReadAheadPages ?? 32)

    const publishFrontierFromState = (): void => {
        publishFrontierStateToSabIfChanged(sabs.sab4, {
            currentWalSalt0: state.currentWalSalt0,
            currentWalSalt1: state.currentWalSalt1,
            integratedFrameMax: state.integratedFrameMax,
        })
    }

    const enterFatalRuntime = (error: unknown, _reason: string): boolean => enterFatalRuntimeState(
        state,
        sabs.sab1,
        sabs.sab4,
        state as FatalWakeState,
        error,
        WRITE_GATE_REASON_LIFECYCLE_FATAL,
        sabs.pageSize
    )

    const captureDurableSnapshotFromState = (): DurableLocalSnapshot => captureDurableSnapshot(state, {
        requiredBlobHashes: state.stagedCas.keys(),
        requiredManifestNodeHashes: state.stagedManifestNodes.keys(),
    })

    const requestPersistLocalSnapshotFromState = (): Promise<void> | undefined => requestPersistLocalSnapshot({
        state,
        runtime: state,
        durable: options.durable,
        captureDurableSnapshot: captureDurableSnapshotFromState,
        onPersistError: error => {
            options.onSnapshotPersistError?.(error)
            enterFatalRuntime(error, SNAPSHOT_PERSIST_FATAL_REASON)
        },
    })

    const advanceDbHead = (): WalSnapshot => {
        const live = options.readLiveWalSnapshot?.() ?? options.tryReadWalSnapshotStableOrNull()
        if (live == null) {
            return state.lastWalSnapshot ?? {
                salt0: state.currentWalSalt0 ?? 0,
                salt1: state.currentWalSalt1 ?? 0,
                iChange: 0,
                mxFrame: state.integratedFrameMax,
                nBackfill: state.integratedFrameMax,
            }
        }

        acceptWalCycleFromSnapshot(
            sabs.sab1,
            state,
            publishFrontierFromState,
            live,
            sabs.pageSize
        )

        const dbPageCountRef = { value: state.dbPageCount }
        applyBackfilledPending(
            sabs.sab1,
            state.pendingDbDeltas,
            state.dbOverlay,
            dbPageCountRef,
            live,
            state.integratedFrameMax,
            sabs.pageSize
        )
        state.dbPageCount = dbPageCountRef.value

        if (
            state.pendingDbDeltas.size === 0
            && state.currentWalSalt0 != null
            && sameWalSalt(state.currentWalSalt0, state.currentWalSalt1, live.salt0, live.salt1)
            && live.mxFrame > state.integratedFrameMax
        ) {
            state.integratedFrameMax = live.mxFrame
            publishFrontierFromState()
        }

        state.lastWalSnapshot = live
        return live
    }

    const launchCheckpointIfNeededFromState = async (): Promise<boolean> => launchCheckpointIfNeeded(
        state.runtimeEpoch,
        state,
        {
            beginCheckpointQuiescentWindow: () => beginCheckpointQuiescentWindow({
                state,
                runtime: state,
                sab4: sabs.sab4,
                hooks: {
                    advanceDbHead,
                    yieldOneTaskTurnForPendingCommitMessages: options.yieldOneTaskTurnForPendingCommitMessages,
                    enterFatalRuntime,
                },
            }),
            hasWalWriteHolders: () =>
                Atomics.load(sab4ScalarView(sabs.sab4), Sab4Scalar.WAL_WRITE_HOLDERS) > 0,
            hasMainDbReaders: () =>
                Atomics.load(sab4ScalarView(sabs.sab4), Sab4Scalar.MAIN_DB_READER_COUNT) > 0,
            postRunCheckpoint: options.postRunCheckpoint,
        },
        maxPassiveCheckpointNoProgress,
        state.checkpointNoProgressCount
    )

    const maybeRequestCheckpoint = async (_reason: string): Promise<boolean> => {
        if (state.syncFatalError != null) {
            return false
        }
        const currentWalBytes = Atomics.load(sab2SizeView(sabs.sab2), 0)
        const maxWalBytes = sab2MaxWalByteLength(sabs.sab2)
        const mode: CheckpointMode = currentWalBytes >= Math.floor(maxWalBytes * WAL_TRUNCATE_CHECKPOINT_THRESHOLD_RATIO)
            ? 'TRUNCATE'
            : 'PASSIVE'
        state.checkpointWanted = true
        state.checkpointWantedMode = maxCheckpointMode(
            state.checkpointWantedMode,
            mode
        )
        try {
            return await launchCheckpointIfNeededFromState()
        } catch (error) {
            enterFatalRuntime(error, CHECKPOINT_LAUNCH_FATAL_REASON)
            return false
        }
    }

    const runtimeHooks: ControlWorkerRuntimeHooks = {
        startBootstrap(previousRuntimeEpoch: bigint | null) {
            const nowMs = options.nowMs?.() ?? Date.now()
            const runtimeEpoch = chooseFreshRuntimeEpoch(previousRuntimeEpoch, nowMs)
            options.onBootstrapProgress?.('Starting sqlite3mt control bootstrap', {
                previousRuntimeEpoch: previousRuntimeEpoch?.toString() ?? null,
                runtimeEpoch: runtimeEpoch.toString(),
            })
            const completion = Promise.resolve(options.loadWalBytesForBootstrap())
                .then(walBytes => {
                    options.onBootstrapProgress?.('Loaded sqlite3mt bootstrap WAL bytes', {
                        bytes: walBytes.byteLength,
                    })
                    return walBytes
                })
                .then(walBytes => runBootstrap({
                    sabs,
                    state,
                    durable: options.durable,
                    hooks: {
                        primeLiveWalViewForBootstrap: options.primeLiveWalViewForBootstrap,
                        tryReadWalSnapshotStableOrNull: options.tryReadWalSnapshotStableOrNull,
                        rpcGetHeadVersioned: options.rpc.rpcGetHeadVersioned,
                        refreshRemoteHead:
                            options.refreshRemoteHeadAfterBootstrap
                            ?? (() => options.kickSyncEngine('bootstrap-remote-refresh')),
                        enterFatalRuntime,
                        captureDurableSnapshot: captureDurableSnapshotFromState,
                        hashPage: options.hashPage,
                        reportProgress: options.onBootstrapProgress,
                    } satisfies BootstrapOrchestratorHooks,
                    walBytes,
                    previousRuntimeEpoch,
                    pageSize: sabs.pageSize,
                    nowMs,
                }))
                .then(result => {
                    options.onBootstrapProgress?.(
                        result.kind === 'SUCCESS'
                            ? 'sqlite3mt control bootstrap completed'
                            : 'sqlite3mt control bootstrap failed'
                    )
                    return result.kind === 'SUCCESS'
                        ? { kind: 'SUCCESS' as const }
                        : { kind: 'FATAL' as const, error: result.error }
                })
            return {
                runtimeEpoch,
                completion,
            }
        },

        async onWalCommitSync(message) {
            await onWalCommitSync({
                msg: {
                    ...message,
                    frameMax: message.frameMax as never,
                },
                runtime: state,
                state,
                sab1: sabs.sab1,
                sab4: sabs.sab4,
                hooks: {
                    durable: options.durable,
                    enterFatalRuntime,
                    requestPersistLocalSnapshot: () => {
                        void requestPersistLocalSnapshotFromState()
                    },
                    maybeRequestCheckpoint: reason => maybeRequestCheckpoint(reason),
                    maybeStartPushOrImport: () => {
                        options.kickSyncEngine('local-commit')
                    },
                    yieldOneTaskTurnForPendingCommitMessages: options.yieldOneTaskTurnForPendingCommitMessages,
                },
                pageSize: sabs.pageSize,
            })
        },

        async onFaultRequest(message) {
            await handleFaultRequests(state, {
                publishFault: (slot, gen, pgno, newState, data) => {
                    publishFaultState(slot, gen, pgno, newState as SlotState.ERROR | SlotState.READY | SlotState.RETRY, data, sabs.sab1, sabs.pageSize)
                },
                readerNeedsFrontierWait,
                advanceDbHead,
                getDbPageHash: pgno => getDbPageHash(
                    pgno,
                    state.dbOverlay,
                    state.dbPageCount,
                    state.dbBaseRoot,
                    options.manifest
                ),
                getBlobFromDurable: options.durable.getBlob.bind(options.durable),
                getBlobFromRpc: options.rpc.rpcGetBlob.bind(options.rpc),
                getBlobsFromRpc: options.rpc.rpcGetBlobs == null
                    ? undefined
                    : async hashes => {
                        const blobs = await options.rpc.rpcGetBlobs!(hashes)
                        return new Map(Array.from(blobs.entries()).map(([hash, bytes]) => [
                            hash as PageHash,
                            bytes,
                        ]))
                    },
                abortIfFatal: () => abortIfFatal(state),
            }, [message])
        },

        async onFaultRequests(messages) {
            await handleFaultRequests(state, {
                publishFault: (slot, gen, pgno, newState, data) => {
                    publishFaultState(slot, gen, pgno, newState as SlotState.ERROR | SlotState.READY | SlotState.RETRY, data, sabs.sab1, sabs.pageSize)
                },
                readerNeedsFrontierWait,
                advanceDbHead,
                getDbPageHash: pgno => getDbPageHash(
                    pgno,
                    state.dbOverlay,
                    state.dbPageCount,
                    state.dbBaseRoot,
                    options.manifest
                ),
                getBlobFromDurable: options.durable.getBlob.bind(options.durable),
                getBlobFromRpc: options.rpc.rpcGetBlob.bind(options.rpc),
                getBlobsFromRpc: options.rpc.rpcGetBlobs == null
                    ? undefined
                    : async hashes => {
                        const blobs = await options.rpc.rpcGetBlobs!(hashes)
                        return new Map(Array.from(blobs.entries()).map(([hash, bytes]) => [
                            hash as PageHash,
                            bytes,
                        ]))
                    },
                abortIfFatal: () => abortIfFatal(state),
            }, messages)
        },

        async onCheckpointDone(message) {
            const accepted = await handleCheckpointDone(
                state.runtimeEpoch,
                state,
                {
                    advanceDbHead,
                    endCheckpointQuiescentWindow: () => endCheckpointQuiescentWindow({
                        state,
                        runtime: state,
                        sab4: sabs.sab4,
                    }),
                    launchCheckpointIfNeeded: launchCheckpointIfNeededFromState,
                    maybeCompactDbBaseRoot: options.maybeCompactDbBaseRoot ?? (() => {}),
                    maybeStartPushOrImport: () => {
                        options.kickSyncEngine('checkpoint-done')
                    },
                },
                message,
                maxPassiveCheckpointNoProgress
            )
            const liveAfter = advanceDbHead()
            ;(state as ControlWorkerRuntimeState & {
                __debugCheckpointLifecycle?: Record<string, unknown>
            }).__debugCheckpointLifecycle = {
                accepted,
                seq: message.seq,
                mode: message.mode,
                rc: message.rc,
                logFrames: message.logFrames,
                ckptFrames: message.ckptFrames,
                checkpointBusy: state.checkpointBusy,
                checkpointWanted: state.checkpointWanted,
                checkpointQuiescentBusy: state.checkpointQuiescentBusy,
                checkpointInflightSeq: state.checkpointInflightSeq,
                liveAfter,
                backlogAfter: Math.max(0, liveAfter.mxFrame - liveAfter.nBackfill),
            }
        },

        async onRemoteHeadUpdate(message) {
            applyRemoteHeadIfNewer(
                state,
                message.root,
                message.version,
                message.reqSeq,
                {
                    maybeStartPushOrImport: () => {
                        options.kickSyncEngine('remote-head-update')
                    },
                    enterFatalRuntime,
                }
            )
        },

        async onSyncRetryFired(_message) {
            options.kickSyncEngine('sync-retry')
        },

        async shutdown() {
            enterFatalRuntime(new Error(SHUTDOWN_FATAL_REASON), SHUTDOWN_FATAL_REASON)
            await options.onShutdown?.()
        },
    }

    const runtime = createControlWorkerRuntime(runtimeHooks)

    return {
        sabs,
        state,
        runtime,
        runtimeHooks,
        advanceDbHead,
        enterFatalRuntime,
        maybeRequestCheckpoint,
        requestPersistLocalSnapshot: requestPersistLocalSnapshotFromState,
    }
}

// ── Re-export SAB helpers for convenience ────────────────────────

export {
    allocateSabSet,
    computeSabSizes,
    type SabConfig,
    type SabSizes,
}
